<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { onMount, untrack } from "svelte"
  import {
    ceilPositiveMinutesToStep,
    SCHEDULING_MINUTE_STEP,
  } from "../../../../../../../_shared/_logic/time-granularity"
  import {
    CUSTOM_ORDER_TYPE_SELECTION,
    catalogOrderTypeIds,
    isCustomOrderTypeSelection,
    orderTypeSelectionValues,
    reconcileOrderTypeSelection,
    selectedOrderTypeItems,
    summedTypicalDurationMinutes,
  } from "../../../../_logic/order-type-selection"

  type OrderTypeValue = {
    order_type_ids: string[]
    custom_order_type: WorkOrderCustomOrderType | null
    estimated_duration_min: string
  }
  type FieldSubset = { orderType: string; estimatedDuration: string }
  type Props = {
    value: OrderTypeValue
    orderTypes?: OrderType[]
    fieldIds: FieldSubset
    brightBackground?: boolean
    surfaceTone?: "auto" | "light" | "dark"
    durationLabelMode?: "duration" | "typical"
    disabled?: boolean
    clearable?: boolean
    onChange?: (detail: ChangeDetail) => void
  }

  type ChangeDetail = {
    value: OrderTypeValue
    valid: boolean
  }

  let {
    value,
    orderTypes = [],
    fieldIds,
    brightBackground = true,
    surfaceTone = "auto",
    durationLabelMode = "duration",
    disabled = false,
    clearable = true,
    onChange = () => {},
  }: Props = $props()

  const toDurationString = (input: unknown) => {
    const rounded = ceilPositiveMinutesToStep(input)

    return rounded === null ? "" : String(rounded)
  }

  const orderTypeOptions = $derived(
    [...orderTypes]
      .sort((a, b) =>
        a.internal_label.localeCompare(b.internal_label, "de", { sensitivity: "base" }),
      )
      .map((type) => ({ name: type.internal_label, value: type.id })),
  )
  const orderTypeSelectOptions = $derived([
    ...orderTypeOptions,
    { name: "Sonstige (freier Text)", value: CUSTOM_ORDER_TYPE_SELECTION },
  ])

  const deriveSelectionState = (input: OrderTypeValue) => {
    const values = orderTypeSelectionValues(input)
    const selectionValues =
      values.length || orderTypeOptions.length ? values : [CUSTOM_ORDER_TYPE_SELECTION]

    return {
      selectionValues,
      duration: toDurationString(input.estimated_duration_min),
      customLabel: input.custom_order_type?.internal_label ?? "",
    }
  }

  const initialSelection = deriveSelectionState(untrack(() => value))

  let selectionValues = $state<string[]>(initialSelection.selectionValues)
  let previousSelectionValues = $state<string[]>([...initialSelection.selectionValues])
  let estimatedDuration = $state(initialSelection.duration)
  let customOrderTypeLabel = $state(initialSelection.customLabel)
  let lastEmitted: ChangeDetail | null = $state(null)

  const typicalDuration = (selectedValues: readonly string[]) =>
    isCustomOrderTypeSelection(selectedValues)
      ? null
      : summedTypicalDurationMinutes(catalogOrderTypeIds(selectedValues), orderTypes)

  const instructionSourceLabel = $derived.by(() => {
    const ids = catalogOrderTypeIds(selectionValues)

    if (ids.length < 2) return null

    return (
      selectedOrderTypeItems({ order_type_ids: ids, custom_order_type: null }, orderTypes)[0]
        ?.internal_label ?? null
    )
  })

  const getEstimatedDurationPlaceholder = (selectedValues: readonly string[]) => {
    const duration = typicalDuration(selectedValues)

    if (duration === null) return "Dauer (Minuten)"

    return durationLabelMode === "typical"
      ? `Typische Dauer: ${duration} Minuten`
      : `Dauer: ${duration} Minuten`
  }

  const sameValue = (left: OrderTypeValue, right: OrderTypeValue) =>
    left.order_type_ids.length === right.order_type_ids.length &&
    left.order_type_ids.every((id, index) => id === right.order_type_ids[index]) &&
    left.custom_order_type?.internal_label === right.custom_order_type?.internal_label &&
    left.custom_order_type?.public_label === right.custom_order_type?.public_label &&
    left.estimated_duration_min === right.estimated_duration_min

  const emitChange = () => {
    const customMode = isCustomOrderTypeSelection(selectionValues)
    const orderTypeIds = customMode ? [] : catalogOrderTypeIds(selectionValues)
    const customLabel = customOrderTypeLabel.trim()
    const currentDuration = toDurationString(estimatedDuration)
    const defaultDuration = typicalDuration(selectionValues)
    const durationValue =
      currentDuration || (defaultDuration === null ? "" : String(defaultDuration))
    const detail: ChangeDetail = {
      value: {
        order_type_ids: orderTypeIds,
        custom_order_type: customMode
          ? { internal_label: customLabel, public_label: customLabel }
          : null,
        estimated_duration_min: durationValue,
      },
      valid: orderTypeIds.length > 0 || Boolean(customMode && customLabel),
    }

    const hasChanged =
      !lastEmitted ||
      lastEmitted.valid !== detail.valid ||
      !sameValue(lastEmitted.value, detail.value)

    if (hasChanged) {
      lastEmitted = detail
      onChange(detail)
    }

    if (durationValue !== currentDuration) {
      estimatedDuration = durationValue
    }
  }

  const handleOrderTypeChange = () => {
    const reconciled = reconcileOrderTypeSelection(previousSelectionValues, selectionValues)
    selectionValues = reconciled
    previousSelectionValues = [...reconciled]

    if (!isCustomOrderTypeSelection(reconciled)) {
      customOrderTypeLabel = ""
      estimatedDuration = String(typicalDuration(reconciled) ?? "")
    }

    emitChange()
  }

  onMount(() => {
    emitChange()
  })
</script>

<section class="row">
  <Input
    id={fieldIds.orderType}
    type="select"
    multiple={true}
    {clearable}
    options={orderTypeSelectOptions}
    placeholder="Auftragstyp wählen"
    bind:value={selectionValues}
    tooltip="Bestimmt die Bezeichnung und Standarddauer. Auftragstypen verwalten Sie in den Einstellungen."
    onChange={handleOrderTypeChange}
    {brightBackground}
    {surfaceTone}
    {disabled}
  />

  {#if isCustomOrderTypeSelection(selectionValues)}
    <Input
      id={`${fieldIds.orderType}-custom`}
      placeholder="Auftragstyp (freier Text)"
      tooltip="Optionale Bezeichnung für den benutzerdefinierten Auftragstyp."
      bind:value={customOrderTypeLabel}
      {disabled}
      {brightBackground}
      {surfaceTone}
      onChange={emitChange}
    />
  {/if}

  <Input
    id={fieldIds.estimatedDuration}
    type="number"
    placeholder={getEstimatedDurationPlaceholder(selectionValues)}
    bind:value={estimatedDuration}
    min={SCHEDULING_MINUTE_STEP}
    step={SCHEDULING_MINUTE_STEP}
    onChange={emitChange}
    tooltip="Standarddauer für die Termine dieses Auftrags."
    {disabled}
    {brightBackground}
    {surfaceTone}
  />
</section>
